import { useState, useEffect, useRef } from 'react';
import { Calendar, Layers, BarChart3 } from 'lucide-react';
import BottomNav from '../../bottom-nav.jsx';
import SwipeableTabs from '../../swipeable-tabs.jsx';
import { TroedelHeader } from './troedel-header.jsx';
import { Markttage } from './markttage.jsx';
import { Inventar } from './inventar.jsx';
import { Statistik } from './statistik.jsx';
import { LiveMarkt } from './live-markt.jsx';
import { FleaSettings } from './flea-settings.jsx';
import { useHubData } from '../../shared-modes/use-hub-data.js';
import { getPhotoUrl } from '../../photo-utils.js';
import { PullToRefresh } from '../../components/boothly/index.js';
import { useToast } from '../../components/boothly/toast-provider.jsx';
import { useHaptic } from '../../hooks/use-haptic.js';
import { useConfirm } from '../../components/boothly/confirm-provider.jsx';

const TABS = [
  { id: 'markttage',  label: 'Tage',      icon: <Calendar size={22} /> },
  { id: 'inventar',   label: 'Sortiment',  icon: <Layers size={22} /> },
  { id: 'statistik',  label: 'Statistik',  icon: <BarChart3 size={22} /> },
];

const TAB_MAP = { sessions: 'markttage', inventory: 'inventar', stats: 'statistik' };
const TAB_MAP_REV = { markttage: 'sessions', inventar: 'inventory', statistik: 'stats' };

export function Troedel({
  store,
  authToken,
  openLiveDirectly,
  onBack,
  onStoreDeleted,
  dark,
  userPreferences,
  saveUserPreferences,
}) {
  const toast = useToast();
  const haptic = useHaptic();
  const confirm = useConfirm();
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [liveMarktOpen, setLiveMarktOpen] = useState(false);
  // Live-Verkäufe der laufenden Session — EINE Quelle für Live-Stats + "Verkauft"-Liste.
  // Lebt hier (nicht in LiveMarkt) damit Spontanverkäufe Remounts überleben und in der
  // Markttage-Übersicht auftauchen.
  const [liveSales, setLiveSales] = useState([]);
  // item_ids, die in der aktiven Session bereits verkauft wurden (aus DB rehydriert) —
  // filtert das Sortiment auch ohne den persistenten status='verkauft' (deploy-tolerant).
  const [persistedSoldIds, setPersistedSoldIds] = useState(() => new Set());
  const saleSeqRef = useRef(0);
  const nextSaleId = () => `ls-${++saleSeqRef.current}`;
  const hydratedSessionRef = useRef(null); // welche Session-ID bereits rehydriert wurde
  const deepLinkDoneRef = useRef(false);   // Deep-Link-Auto-Open nur einmal

  const {
    loading,
    items, setItems,
    sessions, setSessions,
    activeSession, setActiveSession,
    categories,
    tab, setTab,
    reloadAll,
    loadMoreSessions,
    loadMoreItems,
    refreshing,
    error,
    settingsOpen, setSettingsOpen,
  } = useHubData({ store, authToken, mode: 'flea' });

  // Deep-Link vom Dashboard: Live-Markt EINMALIG direkt öffnen, sobald die aktive Session
  // geladen ist. !ends_at + One-Shot-Ref verhindern, dass das Beenden den Overlay sofort
  // wieder aufreißt (activeSession trägt optimistisch noch ends_at).
  useEffect(() => {
    if (deepLinkDoneRef.current) return;
    if (openLiveDirectly && activeSession && !activeSession.ends_at) {
      deepLinkDoneRef.current = true;
      setLiveMarktOpen(true);
    }
  }, [openLiveDirectly, activeSession]);

  // Rehydration: Live-Verkäufe der laufenden Session aus der DB holen (GET /sessions/:id),
  // damit Umsatz/Zähler/"Verkauft"-Liste + verkaufte Items einen Reload/Neustart überleben.
  // Einmal pro Session-ID; Dedupe-Merge per Sale-ID schützt vor Verlust optimistischer
  // Verkäufe, die während des Fetches getätigt werden.
  useEffect(() => {
    const sid = activeSession?.id;
    if (!sid || activeSession.ends_at) return;
    if (String(sid).startsWith('temp-')) return; // optimistische Session ohne echte ID
    if (hydratedSessionRef.current === sid) return;
    hydratedSessionRef.current = sid;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/stores/${store.id}/sessions/${sid}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const sales = Array.isArray(data?.sales) ? data.sales : [];
        const active = sales.filter(s => !s.is_voided);
        const entries = active
          .slice()
          .sort((a, b) => new Date(a.sold_at || 0) - new Date(b.sold_at || 0))
          .map(s => {
            const si = s.sale_items || [];
            const primary = si[0];
            const isSpontan = si.length > 0 && !primary?.item_id;
            const isBundle = s.is_bundle && si.length > 1;
            return {
              id: s.id,
              kind: isBundle ? 'bundle' : isSpontan ? 'spontan' : 'item',
              name: isBundle ? `Bundle (${si.length} Artikel)` : (primary?.item_name || 'Spontan-Verkauf'),
              price: s.total,
              original: (!isBundle && !isSpontan) ? (primary?.wish_price ?? null) : null,
            };
          });
        const soldIds = new Set(active.flatMap(s => (s.sale_items || []).map(i => i.item_id).filter(Boolean)));
        if (cancelled) return;
        // Dedupe-Merge: Server-History als Basis, optimistische Einträge die noch NICHT
        // in der Server-Antwort sind oben drauf (NICHT all-or-nothing).
        setLiveSales(prev => {
          const ids = new Set(entries.map(e => e.id));
          return [...entries, ...prev.filter(p => !ids.has(p.id))];
        });
        setPersistedSoldIds(prev => new Set([...prev, ...soldIds]));
      } catch { /* Rehydration non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [activeSession?.id, activeSession?.ends_at, store.id, authToken]);

  // Map hub tab ids to our local TABS ids
  const activeTab = TAB_MAP[tab] || 'markttage';
  const handleTabChange = (id) => setTab(TAB_MAP_REV[id] || 'sessions');

  // --- Mutation handlers (optimistic) ---

  const handleCreateSession = async (data) => {
    haptic.medium();
    const tempId = `temp-${Date.now()}`;
    // "Für später speichern" markiert die Session als geplant (metadata.planned),
    // damit der Worker sie NICHT als laufenden Markt zurückgibt (GET /sessions/active).
    const metadata = data.startNow ? {} : { planned: true };
    // Bei "Jetzt starten" ist starts_at IMMER der echte Moment des Live-Gehens — NICHT
    // data.date (eine "YYYY-MM-DD"-Eingabe würde als UTC-Mitternacht geparst → Timer backdated).
    // Geplante Märkte behalten ihr Datum; handleStartPlanned setzt starts_at später auf now.
    const startsAt = data.startNow
      ? new Date().toISOString()
      : (data.date ? new Date(data.date).toISOString() : new Date().toISOString());
    const tempSession = {
      id: tempId, ...data,
      name: data.location,
      location: data.location,
      starts_at: startsAt,
      ends_at: data.startNow ? null : undefined,
      startTime: data.startNow ? startsAt : undefined,
      booth_fee: data.booth_fee ?? null, // Break-even greift sofort beim "Jetzt starten"
      metadata,
    };
    if (data.startNow) {
      setActiveSession(tempSession);
      setLiveSales([]); // frische Live-Session
      setPersistedSoldIds(new Set());
    } else {
      setSessions(prev => [tempSession, ...prev]);
    }
    const res = await fetch(`/api/stores/${store.id}/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ location: data.location, starts_at: startsAt, notes: data.notes, booth_fee: data.booth_fee ?? null, metadata }),
    });
    if (!res.ok) {
      if (data.startNow) setActiveSession(null);
      else setSessions(prev => prev.filter(s => s.id !== tempId));
      toast.error('Markttag konnte nicht erstellt werden');
    } else {
      const created = await res.json();
      if (data.startNow) {
        setActiveSession(created);
        toast.success('Markt gestartet');
      } else {
        setSessions(prev => prev.map(s => s.id === tempId ? created : s));
        toast.success('Markttag angelegt');
      }
    }
  };

  // Startet eine geplante (für später gespeicherte) Session: entfernt das planned-Flag
  // und setzt starts_at auf jetzt → wird zum laufenden Markt.
  const handleStartPlanned = async (session) => {
    haptic.medium();
    const nowIso = new Date().toISOString();
    const newMeta = { ...(session.metadata || {}), planned: false };
    const prevSessions = sessions;
    setSessions(prev => prev.filter(s => s.id !== session.id));
    setActiveSession({ ...session, metadata: newMeta, starts_at: nowIso, ends_at: null });
    setLiveSales([]); // frische Live-Session
    setPersistedSoldIds(new Set());
    setLiveMarktOpen(true);
    const res = await fetch(`/api/stores/${store.id}/sessions/${session.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ metadata: newMeta, starts_at: nowIso }),
    });
    if (!res.ok) {
      setActiveSession(null);
      setLiveMarktOpen(false);
      setSessions(prevSessions);
      toast.error('Markt konnte nicht gestartet werden');
    } else {
      const updated = await res.json();
      setActiveSession(updated);
      toast.success('Markt gestartet');
    }
  };

  const handleEndSession = async () => {
    if (!activeSession) return;
    const ok = await confirm({
      title: 'Markt beenden?',
      subtitle: 'Der laufende Markttag wird abgeschlossen.',
      confirmLabel: 'Markt beenden',
      destructive: false,
    });
    if (!ok) return;
    haptic.heavy();
    const prev = activeSession;
    setActiveSession(s => ({ ...s, ends_at: new Date().toISOString() }));
    setLiveMarktOpen(false);
    setTab('sessions'); // Auto-Navigation zur Tage-Ansicht (gleiches Ziel wie Zurück-Pfeil)
    const res = await fetch(`/api/stores/${store.id}/sessions/${activeSession.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ ends_at: new Date().toISOString() }),
    });
    if (!res.ok) {
      setActiveSession(prev);
      setLiveMarktOpen(true);
      toast.error('Session konnte nicht beendet werden');
    } else {
      setSessions(sess => [{ ...prev, ends_at: new Date().toISOString() }, ...sess]);
      toast.success('Markt beendet');
      setLiveSales([]);
      setPersistedSoldIds(new Set());
      hydratedSessionRef.current = null; // nächste Session wird neu rehydriert
      await reloadAll();
    }
  };

  // Standgebühr eines Markttags nachträglich setzen/ändern/entfernen (null = entfernen).
  // Optimistisch in sessions UND activeSession patchen → Detail + Statistik rechnen sofort neu.
  const handleUpdateBoothFee = async (sessionId, boothFee) => {
    haptic.light();
    const prevSessions = sessions;
    const prevActive = activeSession;
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, booth_fee: boothFee } : s));
    setActiveSession(s => (s && s.id === sessionId ? { ...s, booth_fee: boothFee } : s));
    const res = await fetch(`/api/stores/${store.id}/sessions/${sessionId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ booth_fee: boothFee }),
    });
    if (!res.ok) {
      setSessions(prevSessions);
      setActiveSession(prevActive);
      toast.error('Standgebühr konnte nicht gespeichert werden');
      return false;
    }
    toast.success('Standgebühr gespeichert');
    return true;
  };

  const handleQuickSale = async (item, price) => {
    haptic.success();
    const prevItems = items;
    const entryId = nextSaleId();
    const original = item.wish_price != null ? item.wish_price : item.price;
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, _soldThisMarkt: true, sold: true, soldPrice: price } : i));
    setLiveSales(prev => [...prev, { id: entryId, kind: 'item', name: item.name, price, original }]);
    const res = await fetch(`/api/stores/${store.id}/sessions/${activeSession.id}/sales`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({
        items: [{ item_id: item.id, item_name: item.name, quantity: 1, unit_price: price, wish_price: item.wish_price != null ? item.wish_price : item.price }],
        total: price, payment: 'cash',
      }),
    });
    if (!res.ok) {
      setItems(prevItems);
      setLiveSales(prev => prev.filter(e => e.id !== entryId));
      toast.error('Verkauf fehlgeschlagen');
    } else {
      // Optimistische Temp-id durch echte sale.id ersetzen → Rehydration-Dedupe
      const created = await res.json().catch(() => null);
      if (created?.id) setLiveSales(prev => prev.map(e => e.id === entryId ? { ...e, id: created.id } : e));
      toast.success(`${item.name} verkauft`);
    }
  };

  const handleBundleSale = async (bundleItems, bundlePrice) => {
    haptic.success();
    const prevItems = items;
    const entryId = nextSaleId();
    const ids = new Set(bundleItems.map(i => i.id));
    setItems(prev => prev.map(i => ids.has(i.id) ? { ...i, _soldThisMarkt: true, sold: true } : i));
    setLiveSales(prev => [...prev, { id: entryId, kind: 'bundle', name: `Bundle (${bundleItems.length} Artikel)`, price: bundlePrice }]);
    const res = await fetch(`/api/stores/${store.id}/sessions/${activeSession.id}/sales`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({
        items: bundleItems.map(i => ({ item_id: i.id, item_name: i.name, quantity: 1, unit_price: bundlePrice / bundleItems.length, wish_price: i.wish_price != null ? i.wish_price : i.price })),
        total: bundlePrice, payment: 'cash', is_bundle: true,
      }),
    });
    if (!res.ok) {
      setItems(prevItems);
      setLiveSales(prev => prev.filter(e => e.id !== entryId));
      toast.error('Bundle-Verkauf fehlgeschlagen');
    } else {
      const created = await res.json().catch(() => null);
      if (created?.id) setLiveSales(prev => prev.map(e => e.id === entryId ? { ...e, id: created.id } : e));
      toast.success(`${bundleItems.length} Artikel verkauft`);
    }
  };

  const handleSpontanSale = async (price, note) => {
    haptic.success();
    const entryId = nextSaleId();
    setLiveSales(prev => [...prev, { id: entryId, kind: 'spontan', name: note || 'Spontan-Verkauf', price }]);
    const res = await fetch(`/api/stores/${store.id}/sessions/${activeSession.id}/sales`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ items: [{ item_name: note || 'Spontan-Verkauf', quantity: 1, unit_price: price, wish_price: price }], total: price, payment: 'cash', notes: note || '' }),
    });
    if (!res.ok) {
      setLiveSales(prev => prev.filter(e => e.id !== entryId));
      toast.error('Spontanverkauf fehlgeschlagen');
    } else {
      const created = await res.json().catch(() => null);
      if (created?.id) setLiveSales(prev => prev.map(e => e.id === entryId ? { ...e, id: created.id } : e));
      toast.success('Spontanverkauf gespeichert');
    }
  };

  // Storno eines Live-Verkaufs (Overnight-Redesign, bewusst separater Eingriff — einzeln revertierbar).
  // Nutzt die bestehende Worker-Logik PATCH /sales/:id { is_voided: true } (setzt das Item serverseitig
  // wieder auf 'verfuegbar'). Die bestehenden Sale-Handler + Rehydration bleiben unverändert: die
  // betroffenen item_ids kommen aus GET /sessions/:id, weil liveSales-Einträge keine item_ids tragen.
  const handleVoidSale = async (entry) => {
    if (!entry?.id || !activeSession?.id) return false;
    if (String(entry.id).startsWith('ls-')) {
      toast.info('Verkauf wird noch gespeichert', 'Bitte gleich noch einmal versuchen.');
      return false;
    }
    haptic.medium();
    const prevSales = liveSales;
    setLiveSales(prev => prev.filter(e => e.id !== entry.id));
    try {
      const detailRes = await fetch(`/api/stores/${store.id}/sessions/${activeSession.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const detail = detailRes.ok ? await detailRes.json().catch(() => null) : null;
      const sale = (detail?.sales || []).find(s => s.id === entry.id);
      const itemIds = (sale?.sale_items || []).map(i => i.item_id).filter(Boolean);
      const res = await fetch(`/api/stores/${store.id}/sales/${entry.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ is_voided: true }),
      });
      if (!res.ok) throw new Error(`void ${res.status}`);
      if (itemIds.length) {
        const ids = new Set(itemIds);
        setItems(prev => prev.map(i => ids.has(i.id)
          ? { ...i, sold: false, _soldThisMarkt: false, soldPrice: undefined, status: 'verfuegbar' }
          : i));
        setPersistedSoldIds(prev => new Set([...prev].filter(id => !ids.has(id))));
      }
      toast.success('Verkauf storniert', itemIds.length ? 'Artikel ist wieder im Sortiment.' : undefined);
      return true;
    } catch {
      setLiveSales(prevSales);
      toast.error('Storno fehlgeschlagen');
      return false;
    }
  };

  const handleCreateItem = async (data, photoBlob) => {
    haptic.medium();
    const tempId = `temp-${Date.now()}`;
    setItems(prev => [{ id: tempId, ...data, active: true, _uploading: true }, ...prev]);
    const res = await fetch(`/api/stores/${store.id}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ name: data.name, price: data.price, wish_price: data.wish_price, metadata: { category: data.category, notes: data.notes }, active: true }),
    });
    if (!res.ok) {
      setItems(prev => prev.filter(i => i.id !== tempId));
      toast.error('Item konnte nicht erstellt werden');
      return;
    }
    const created = await res.json();
    let imageKey = created.image_key || null;
    if (photoBlob) {
      try {
        const { uploadPhoto } = await import('../../photo-utils.js');
        const up = await uploadPhoto(authToken, store.id, created.id, photoBlob);
        if (up?.key) imageKey = up.key; // Worker hat image_key serverseitig gesetzt
      } catch (_e) { /* photo upload failure is non-fatal */ }
    }
    // image_key + Cache-Bust direkt setzen → Bild sofort sichtbar, ohne Reload
    setItems(prev => prev.map(i => i.id === tempId ? { ...created, image_key: imageKey, _photoV: Date.now() } : i));
    toast.success('Item angelegt');
  };

  // Foto zu einem bestehenden Item hinzufügen/ersetzen (Edit-Sheet). Setzt image_key +
  // Cache-Bust direkt in den State → sofort sichtbar, ohne Reload.
  const handleUploadItemPhoto = async (itemId, blob) => {
    try {
      const { uploadPhoto } = await import('../../photo-utils.js');
      const up = await uploadPhoto(authToken, store.id, itemId, blob);
      if (up?.key) {
        setItems(prev => prev.map(i => i.id === itemId ? { ...i, image_key: up.key, _photoV: Date.now() } : i));
      }
      toast.success('Foto gespeichert');
      return true;
    } catch (_e) {
      toast.error('Foto-Upload fehlgeschlagen');
      return false;
    }
  };

  const handleUpdateItem = async (id, patch) => {
    haptic.light();
    const prev = items.find(i => i.id === id);
    setItems(p => p.map(i => i.id === id ? { ...i, ...patch } : i));
    const res = await fetch(`/api/stores/${store.id}/items/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      setItems(p => p.map(i => i.id === id ? prev : i));
      toast.error('Änderung konnte nicht gespeichert werden');
    } else {
      toast.success('Gespeichert');
    }
  };

  const handleDeleteItem = async (id) => {
    haptic.heavy();
    const prev = items.find(i => i.id === id);
    setItems(p => p.filter(i => i.id !== id));
    const res = await fetch(`/api/stores/${store.id}/items/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ active: false }),
    });
    if (!res.ok) {
      setItems(p => [prev, ...p]);
      toast.error('Löschen fehlgeschlagen');
    }
  };

  // Derive running/past/planned markets from activeSession and sessions
  const runningMarket = (activeSession && !activeSession.ends_at) ? activeSession : null;
  // Live-Stats aus der einen Quelle (liveSales) — speist Banner + Live-Markt-Kopf
  const liveRevenue = liveSales.reduce((sum, e) => sum + (e.price || 0), 0);
  const liveCount = liveSales.length;
  const runningMarketWithStats = runningMarket
    ? { ...runningMarket, revenue: liveRevenue, salesCount: liveCount }
    : null;
  const pastMarkets = (sessions || []).filter(s => s.ends_at);
  const plannedMarkets = (sessions || []).filter(
    s => !s.ends_at && s?.metadata?.planned === true && s.id !== activeSession?.id
  );

  return (
    <div
      className={`area-troedel${dark ? ' troedel-dark' : ''}`}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)',
      }}
    >
      <TroedelHeader
        title={store?.name ?? 'Flohmarkt'}
        onBack={onBack}
        onSettings={() => setSettingsOpen(true)}
        dark={dark}
      />

      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <SwipeableTabs
          tabs={TABS}
          activeTab={activeTab}
          onChange={handleTabChange}
          onSwipeProgress={setSwipeProgress}
          disabled={liveMarktOpen}
        >
          {/* Tab 0: Markttage */}
          <PullToRefresh onRefresh={reloadAll}>
            <main className="px-5 pb-32">
              <Markttage
                runningMarket={runningMarketWithStats}
                pastMarkets={pastMarkets}
                plannedMarkets={plannedMarkets}
                onOpenLiveMarkt={() => setLiveMarktOpen(true)}
                onCreateSession={handleCreateSession}
                onStartPlanned={handleStartPlanned}
                onUpdateBoothFee={handleUpdateBoothFee}
                dark={dark}
                loading={loading}
                store={store}
                authToken={authToken}
              />
            </main>
          </PullToRefresh>

          {/* Tab 1: Inventar */}
          <PullToRefresh onRefresh={reloadAll}>
            <main className="px-5 pb-32">
              <Inventar
                items={items || []}
                categories={categories || []}
                onCreateItem={handleCreateItem}
                onUpdateItem={handleUpdateItem}
                onDeleteItem={handleDeleteItem}
                onUploadItemPhoto={handleUploadItemPhoto}
                getPhotoUrl={getPhotoUrl}
                dark={dark}
                store={store}
                authToken={authToken}
                loading={loading}
                runningMarket={runningMarket}
              />
            </main>
          </PullToRefresh>

          {/* Tab 2: Statistik */}
          <PullToRefresh onRefresh={reloadAll}>
            <main className="px-5 pb-32">
              <Statistik
                sessions={sessions || []}
                dark={dark}
                loading={loading}
              />
            </main>
          </PullToRefresh>
        </SwipeableTabs>
      </div>

      {/* BottomNav — hidden during LiveMarkt */}
      {!liveMarktOpen && (
        <BottomNav
          tabs={TABS}
          activeTab={activeTab}
          onChange={handleTabChange}
          accent="flea"
          dark={dark}
          swipeProgress={swipeProgress}
        />
      )}

      {/* Settings sheet */}
      {settingsOpen && (
        <FleaSettings
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          store={store}
          authToken={authToken}
          dark={dark}
          onStoreDeleted={onStoreDeleted}
          categories={categories || []}
          onCategoriesChanged={reloadAll}
          itemsCount={(items || []).length}
          sessionsCount={(sessions || []).length}
        />
      )}

      {/* LiveMarkt full-screen overlay */}
      {liveMarktOpen && (
        <LiveMarkt
          session={activeSession}
          items={(items || []).filter(i => i.active !== false)}
          liveSales={liveSales}
          persistedSoldIds={persistedSoldIds}
          onQuickSale={handleQuickSale}
          onBundleSale={handleBundleSale}
          onSpontanSale={handleSpontanSale}
          onVoidSale={handleVoidSale}
          onEndSession={handleEndSession}
          onClose={() => setLiveMarktOpen(false)}
          dark={dark}
          getPhotoUrl={getPhotoUrl}
        />
      )}
    </div>
  );
}
